import { allByType, changes } from './store-adapter';
import type {
  Customer,
  DeviceType,
  HoseConfig,
  Manufacturer,
  ServicePrice,
  Settings,
  SparePart,
  Visit,
  Worker,
} from '@/data/types';

/**
 * Initial state read from PouchDB on auth. Returns a partial slice the caller
 * can merge into the Zustand store (typically via useApp.setState(partial)).
 *
 * Reads each CouchDB doc type that the FE shadow-puts (apps/frontend/src/store/index.ts).
 * For singleton docs (settings, hose_config) returns the first match.
 * Empty result for a type leaves that slice undefined so the caller's existing
 * seed / localStorage state is preserved.
 *
 * Errors are swallowed so a first-run user (no PouchDB data yet) still boots
 * with seeded state.
 */
export interface InitPartial {
  customers?: Customer[];
  manufacturers?: Manufacturer[];
  deviceTypes?: DeviceType[];
  spareParts?: SparePart[];
  servicePrices?: ServicePrice[];
  visits?: Visit[];
  workers?: Worker[];
  settings?: Settings;
  hoseConfig?: HoseConfig;
}

type AnyDoc = Record<string, unknown> & {
  _id?: string;
  _rev?: string;
  type: string;
  tenant_id?: string;
};

function stripMeta<T>(doc: AnyDoc): T {
  const { _id, _rev, type, tenant_id, ...rest } = doc;
  void _id;
  void _rev;
  void type;
  void tenant_id;
  return rest as T;
}

export async function initStoreFromPouchDB(): Promise<InitPartial> {
  try {
    const [
      customerDocs,
      manufacturerDocs,
      deviceTypeDocs,
      sparePartDocs,
      servicePriceDocs,
      visitDocs,
      workerDocs,
      settingsDocs,
      hoseConfigDocs,
    ] = await Promise.all([
      allByType<AnyDoc>('customer'),
      allByType<AnyDoc>('manufacturer'),
      allByType<AnyDoc>('device_type'),
      allByType<AnyDoc>('spare_part'),
      allByType<AnyDoc>('service_price'),
      allByType<AnyDoc>('visit'),
      allByType<AnyDoc>('worker'),
      allByType<AnyDoc>('settings'),
      allByType<AnyDoc>('hose_config'),
    ]);

    const out: InitPartial = {};
    if (customerDocs.length)
      out.customers = customerDocs.map(stripMeta<Customer>);
    if (manufacturerDocs.length)
      out.manufacturers = manufacturerDocs.map(stripMeta<Manufacturer>);
    if (deviceTypeDocs.length)
      out.deviceTypes = deviceTypeDocs.map(stripMeta<DeviceType>);
    if (sparePartDocs.length)
      out.spareParts = sparePartDocs.map(stripMeta<SparePart>);
    if (servicePriceDocs.length)
      out.servicePrices = servicePriceDocs.map(stripMeta<ServicePrice>);
    if (visitDocs.length) out.visits = visitDocs.map(stripMeta<Visit>);
    if (workerDocs.length) out.workers = workerDocs.map(stripMeta<Worker>);
    if (settingsDocs.length)
      out.settings = stripMeta<Settings>(settingsDocs[0]);
    if (hoseConfigDocs.length)
      out.hoseConfig = stripMeta<HoseConfig>(hoseConfigDocs[0]);

    return out;
  } catch {
    return {};
  }
}

/**
 * Subscribe to PouchDB changes for catalog/entity doc types and feed
 * incoming docs into the supplied Zustand setter. Returns an unsubscribe
 * fn. The one-shot initStoreFromPouchDB call captures whatever has
 * replicated by then; this listener keeps the store live as the initial
 * pull arrives doc-by-doc and on subsequent sync events.
 */
const CATALOG_TYPES = [
  'customer',
  'manufacturer',
  'device_type',
  'spare_part',
  'service_price',
  'visit',
  'worker',
  'settings',
  'hose_config',
];

export function subscribeStoreUpdates(
  apply: (partial: InitPartial) => void
): () => void {
  let pending: ReturnType<typeof setTimeout> | null = null;
  const refresh = () => {
    if (pending) clearTimeout(pending);
    // Debounce a burst of changes — re-read once after 150ms of quiet.
    pending = setTimeout(() => {
      pending = null;
      void initStoreFromPouchDB().then(partial => {
        if (Object.keys(partial).length) apply(partial);
      });
    }, 150);
  };
  return changes(CATALOG_TYPES, refresh);
}
